import React from "react";
import { Award, BookOpen, Calculator, FlaskConical, Languages, PenLine, Sparkles, MessagesSquare, Globe, Heart, HandHeart } from "lucide-react";

const ICONS = {
  award: Award, "book-open": BookOpen, calculator: Calculator,
  "flask-conical": FlaskConical, languages: Languages, "pen-line": PenLine, sparkles: Sparkles,
  "messages-square": MessagesSquare, globe: Globe, heart: Heart, "hand-heart": HandHeart,
};

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
}

/**
 * Printable achievement certificate. Uses inline styles so it renders identically
 * inside a hidden iframe/print window (Tailwind classes may not be loaded there).
 */
export default function Certificate({ cert, studentName }) {
  const color = cert.subject_color || "#0D9488";
  const Icon = ICONS[cert.icon] || Award;
  const kindLabel = {
    lesson: "Lesson Achievement", subject: "Subject Champion",
    level: "Cursive Level Complete", cursive: "Cursive Course Complete",
  }[cert.kind] || "Achievement";
  return (
    <div data-testid={`certificate-${cert.id}`} className="cert-print"
      style={{
        position: "relative", width: "100%", maxWidth: 900, aspectRatio: "1.414/1",
        margin: "0 auto", background: "#FAF7F2",
        border: `10px solid ${color}`, borderRadius: 24,
        padding: "56px 64px", boxShadow: "0 20px 40px rgba(31,41,55,0.12)",
        fontFamily: '"Georgia", "Times New Roman", serif', color: "#1F2937",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
      }}>
      {/* corner ornaments */}
      {[["top", "left"], ["top", "right"], ["bottom", "left"], ["bottom", "right"]].map(([v, h]) => (
        <div key={`${v}-${h}`} style={{
          position: "absolute", [v]: 18, [h]: 18, width: 44, height: 44,
          border: `3px solid ${color}`, borderRadius: 12, opacity: 0.35,
          [`border${v === "top" ? "Bottom" : "Top"}Width`]: 0,
          [`border${h === "left" ? "Right" : "Left"}Width`]: 0,
        }} />
      ))}

      <header style={{ textAlign: "center" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 14px", borderRadius: 999,
          background: `${color}1A`, color, fontFamily: "'Nunito', sans-serif",
          fontWeight: 800, fontSize: 12, letterSpacing: 2, textTransform: "uppercase",
        }}>
          <Icon size={14} /> ZoodLeap · {kindLabel}
        </div>
        <div style={{ marginTop: 22, fontFamily: "'Nunito', sans-serif", fontSize: 14, letterSpacing: 6, color: "#6B7280", textTransform: "uppercase" }}>
          Certificate of Achievement
        </div>
      </header>

      <section style={{ textAlign: "center" }}>
        <div style={{ fontSize: 16, color: "#6B7280", fontStyle: "italic" }}>This certificate is proudly presented to</div>
        <h1 style={{
          margin: "12px 0 20px", fontSize: 56, lineHeight: 1.05, fontWeight: 700,
          fontFamily: '"Great Vibes","Brush Script MT",cursive', color,
        }} data-testid="cert-student">
          {studentName || "Student"}
        </h1>
        <div style={{ fontSize: 20, color: "#1F2937", lineHeight: 1.5, maxWidth: 640, margin: "0 auto" }}>
          for successfully completing
        </div>
        <div style={{
          marginTop: 10, fontFamily: "'Nunito', sans-serif",
          fontSize: 30, fontWeight: 800, color: "#1F2937",
        }} data-testid="cert-title">
          {cert.title}
        </div>
        {cert.subtitle && (
          <div style={{ marginTop: 6, fontSize: 15, color: "#6B7280" }}>{cert.subtitle}</div>
        )}
      </section>

      <footer style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24 }}>
        <div>
          <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 22, fontWeight: 800, color: "#1F2937" }}>{fmtDate(cert.earned_at)}</div>
          <div style={{ marginTop: 4, borderTop: `2px solid ${color}`, paddingTop: 4, fontSize: 12, color: "#6B7280", letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Nunito', sans-serif" }}>
            Date Awarded
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: '"Great Vibes","Brush Script MT",cursive', fontSize: 32, color, lineHeight: 1 }}>ZoodLeap Learning</div>
          <div style={{ marginTop: 4, borderTop: `2px solid ${color}`, paddingTop: 4, fontSize: 12, color: "#6B7280", letterSpacing: 2, textTransform: "uppercase", fontFamily: "'Nunito', sans-serif" }}>
            Learning Coach
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Open a print-ready window with the given certificate. */
export function printCertificate({ cert, studentName }) {
  const w = window.open("", "_blank", "width=1000,height=760");
  if (!w) return;
  const html = `
<!doctype html><html><head><meta charset="utf-8">
<title>${(cert.title || "Certificate").replace(/</g, "&lt;")} — ${(studentName || "Student").replace(/</g, "&lt;")}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Nunito:wght@600;800&display=swap" rel="stylesheet">
<style>
  @page { size: A4 landscape; margin: 10mm; }
  html,body{margin:0;padding:0;background:#F2ECE1;font-family:'Nunito',sans-serif;}
  .wrap{padding:20px;display:flex;align-items:center;justify-content:center;min-height:100vh;}
  .cert{
    position:relative;width:100%;max-width:1050px;aspect-ratio:1.414/1;
    background:#FAF7F2;border:10px solid ${cert.subject_color || "#0D9488"};
    border-radius:24px;padding:56px 64px;box-shadow:0 20px 40px rgba(31,41,55,0.12);
    color:#1F2937;display:flex;flex-direction:column;justify-content:space-between;
    font-family:'Georgia','Times New Roman',serif;
  }
  .kicker{
    display:inline-flex;align-items:center;gap:8px;padding:6px 14px;border-radius:999px;
    background:${cert.subject_color || "#0D9488"}1A;color:${cert.subject_color || "#0D9488"};
    font-family:'Nunito',sans-serif;font-weight:800;font-size:12px;letter-spacing:2px;text-transform:uppercase;
  }
  h1.name{margin:12px 0 20px;font-size:64px;line-height:1.05;font-weight:700;
    font-family:'Great Vibes',cursive;color:${cert.subject_color || "#0D9488"};}
  .title{margin-top:10px;font-family:'Nunito',sans-serif;font-size:32px;font-weight:800;color:#1F2937;}
  .subtitle{margin-top:6px;font-size:15px;color:#6B7280;}
  .cap{font-family:'Nunito',sans-serif;font-size:14px;letter-spacing:6px;color:#6B7280;text-transform:uppercase;}
  .row{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;}
  .row .val{font-family:'Nunito',sans-serif;font-size:22px;font-weight:800;color:#1F2937;}
  .row .lbl{margin-top:4px;border-top:2px solid ${cert.subject_color || "#0D9488"};padding-top:4px;
    font-size:12px;color:#6B7280;letter-spacing:2px;text-transform:uppercase;font-family:'Nunito',sans-serif;}
  .sig{font-family:'Great Vibes',cursive;font-size:36px;color:${cert.subject_color || "#0D9488"};line-height:1;}
  .center{text-align:center;}
  .italic{font-style:italic;color:#6B7280;}
  @media print{ .wrap{padding:0;background:#fff;} .cert{box-shadow:none;} .noprint{display:none!important;} }
</style></head>
<body>
  <div class="wrap">
    <div class="cert">
      <div class="center"><span class="kicker">ZoodLeap · ${(cert.kind === "lesson" ? "Lesson Achievement" : cert.kind === "subject" ? "Subject Champion" : cert.kind === "level" ? "Cursive Level Complete" : "Cursive Course Complete")}</span>
        <div class="cap" style="margin-top:22px">Certificate of Achievement</div></div>
      <div class="center">
        <div class="italic">This certificate is proudly presented to</div>
        <h1 class="name">${(studentName || "Student").replace(/</g, "&lt;")}</h1>
        <div style="font-size:20px">for successfully completing</div>
        <div class="title">${(cert.title || "").replace(/</g, "&lt;")}</div>
        ${cert.subtitle ? `<div class="subtitle">${cert.subtitle.replace(/</g, "&lt;")}</div>` : ""}
      </div>
      <div class="row">
        <div><div class="val">${fmtDate(cert.earned_at)}</div><div class="lbl">Date Awarded</div></div>
        <div style="text-align:right"><div class="sig">ZoodLeap Learning</div><div class="lbl">Learning Coach</div></div>
      </div>
    </div>
  </div>
  <script>window.onload=()=>setTimeout(()=>window.print(),350);<\/script>
</body></html>`;
  w.document.write(html);
  w.document.close();
}
